﻿import { AirportMetarInfo } from '../services/actor-messages';
import {UnitFormatter} from "../formatters";

export interface RunwayAxisWind {
    // Positive = wind blows in the OBB bearing direction (endA→endB).
    axial: number;
    // Positive = wind blows to the right of OBB bearing direction.
    cross: number;
    windSpeed: number;
    isVariable: boolean;
}

export interface WindRenderParts {
    axialArrow: string;
    axialValue: string;
    crossArrow: string;
    crossValue: string;
}

export function effectiveWindSpeed(metar: AirportMetarInfo, useGust: boolean, gustFactor: number): number | null {
    const sustained = metar.windSpeed ?? null;
    if (!useGust || metar.windGusts == null || sustained == null) {
        return sustained;
    }
    return sustained + (metar.windGusts - sustained) * gustFactor;
}

export function computeRunwayAxisWind(runwayBearingDeg: number, metar: AirportMetarInfo): RunwayAxisWind | null {
    if (metar.windSpeed == null || metar.windSpeed === 0) {
        return null;
    }

    if (metar.windDirection === 'variable' || metar.windDirection == null) {
        return {
            axial: 0,
            cross: metar.windSpeed,
            windSpeed: metar.windSpeed,
            isVariable: metar.windDirection === 'variable'
        };
    }

    const windDir = metar.windDirection; // direction wind is COMING FROM
    const speed = metar.windSpeed;

    // Wind vector points in the direction the wind BLOWS TOWARD = windDir + 180
    const windBlowsToward = (windDir + 180) % 360;
    const angleDiffRad = ((windBlowsToward - runwayBearingDeg) * Math.PI) / 180;

    // axial: positive = wind blows in runwayBearing direction (endA→endB)
    const axial = Math.round(speed * Math.cos(angleDiffRad));
    // cross: positive = wind blows to the right when facing runwayBearing direction
    const cross = Math.round(speed * Math.sin(angleDiffRad));

    return {
        axial,
        cross,
        windSpeed: speed,
        isVariable: false
    };
}

export function getWindRenderParts(wind: RunwayAxisWind | null, readingFlipped: boolean): WindRenderParts | null {
    if (!wind) {
        return null;
    }

    if (wind.isVariable) {
        // VRB: no directional arrows, just show the speed as plain text in the axial slot
        return {
            axialArrow: '',
            axialValue: `VRB ${UnitFormatter.formatSpeed(wind.windSpeed)}`,
            crossArrow: '',
            crossValue: ''
        };
    }

    const absAxial = Math.abs(wind.axial);
    const absCross = Math.abs(wind.cross);

    if (absAxial === 0 && absCross === 0) {
        return null;
    }

    // If readingFlipped the rendered text direction is opposite to OBB bearing, so arrows invert.
    const axialPositiveInReading = readingFlipped ? wind.axial < 0 : wind.axial > 0;
    const crossPositiveIsDown    = readingFlipped ? wind.cross < 0 : wind.cross > 0;

    // 🠆 = blows along reading direction   🠄 = blows against reading direction
    const axialArrow = absAxial > 0 ? (axialPositiveInReading ? '🠆' : '🠄') : '';
    // 🠇 = cross pushes right of reading dir   🠅 = cross pushes left of reading dir
    const crossArrow = absCross > 0 ? (crossPositiveIsDown ? '🠇' : '🠅') : '';

    return {
        axialArrow,
        axialValue: absAxial > 0 ? ` ${absAxial.toLocaleString('en-US') + ' kt'}` : '',
        crossArrow,
        crossValue: absCross > 0 ? `${absCross.toLocaleString('en-US') + ' kt'}` : ''
    };
}

export function isReadingFlipped(bearingDeg: number): boolean {
    let r = (bearingDeg - 90) % 360;
    if (r < 0) {
        r += 360;
    }
    return r > 90 && r <= 270;
}

export function runwayAxisRotation(bearingDeg: number): number {
    let r = (bearingDeg - 90) % 360;
    if (r < 0) {
        r += 360;
    }
    // Flip if the text would be upside-down
    if (r > 90 && r <= 270) {
        r = (r + 180) % 360;
    }
    return r;
}
